import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Check, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Stepper } from "@/components/ui/stepper";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableWrapper } from "@/components/ui/table";
import { InfoBanner } from "@/components/ui/banner";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE, localOnly } from "@/lib/fixture-notice";
import { cn, cssVarColor } from "@/lib/utils";
import {
  MOCK_BOOTSTRAP_ADMIN,
  MOCK_ORG_SETUP,
  MOCK_THEME_DEFAULT,
  THEME_COLOR_PRESETS,
  MOCK_ORG_ADMIN_INVITES,
  MOCK_READINESS_CHECKLIST,
  MOCK_READINESS_INCOMPLETE_CHECKLIST,
  ORG_INVITE_ROLES,
} from "@/data/mock";
import type { OrgAdminInvite, ReadinessCheckItem, OrgBootstrapStep } from "@/types";

const STEPS = [
  { label: "Activate", description: "Admin account" },
  { label: "Organization", description: "Entity & contacts" },
  { label: "Branding", description: "Theme & logo" },
  { label: "Admins", description: "Invite quorum" },
  { label: "Readiness", description: "Production gate" },
];

function StepActions({
  primaryLabel = "Continue",
  secondaryLabel = "Back",
  onPrimary,
  onSecondary,
  primaryDisabled = false,
  hideSecondary = false,
}: {
  primaryLabel?: string;
  secondaryLabel?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
  primaryDisabled?: boolean;
  hideSecondary?: boolean;
}) {
  return (
    <div className="mt-8 flex items-center justify-between border-t border-line pt-5">
      {!hideSecondary ? (
        <Button variant="secondary" onClick={onSecondary}>
          {secondaryLabel}
        </Button>
      ) : (
        <span />
      )}
      <Button onClick={onPrimary} disabled={primaryDisabled}>
        {primaryLabel}
      </Button>
    </div>
  );
}

function SectionIntro({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-lg font-extrabold text-ink">{title}</h2>
      <p className="mt-1 text-sm leading-relaxed text-muted">{description}</p>
    </div>
  );
}

function ActivateStep({ onNext }: { onNext: () => void }) {
  const [mfa, setMfa] = useState<"passkey" | "totp">("passkey");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast({ variant: "success", title: "Admin account activated", description: LOCAL_ONLY_NOTICE });
      onNext();
    }, 600);
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionIntro
        title="Activate your admin account"
        description="Complete your account setup to access Alloy. This invitation was sent to your email and expires in 24 hours."
      />

      <div className="space-y-4">
        <Field label="Full Name" htmlFor="activate-name" required>
          <Input id="activate-name" defaultValue={MOCK_BOOTSTRAP_ADMIN.fullName} required />
        </Field>

        <Field label="Email" htmlFor="activate-email">
          <Input
            id="activate-email"
            type="email"
            defaultValue={MOCK_BOOTSTRAP_ADMIN.email}
            readOnly
            className="bg-bg text-muted"
          />
        </Field>

        <Field label="Invite Code" htmlFor="activate-code">
          <Input
            id="activate-code"
            defaultValue={MOCK_BOOTSTRAP_ADMIN.inviteCode}
            readOnly
            className="bg-bg font-mono text-sm text-muted"
          />
        </Field>

        <Field label="Verification Code" htmlFor="activate-verification" hint="Enter the 6-digit code sent to your email.">
          <Input id="activate-verification" placeholder="000000" maxLength={6} required />
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Password" htmlFor="activate-password" required>
            <Input id="activate-password" type="password" placeholder="Min 12 characters" required minLength={12} />
          </Field>
          <Field label="Confirm Password" htmlFor="activate-confirm" required>
            <Input id="activate-confirm" type="password" placeholder="Re-enter password" required />
          </Field>
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold text-ink">Preferred MFA Method</div>
          <div className="space-y-2">
            <label
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors",
                mfa === "passkey" ? "border-primary bg-primary-soft" : "border-line hover:border-muted"
              )}
            >
              <input
                type="radio"
                name="mfa"
                value="passkey"
                checked={mfa === "passkey"}
                onChange={() => setMfa("passkey")}
                className="size-4 accent-primary"
              />
              <div>
                <div className="text-sm font-bold text-ink">Passkey (FIDO2)</div>
                <div className="text-xs text-muted">Biometric or hardware key — most secure</div>
              </div>
            </label>
            <label
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors",
                mfa === "totp" ? "border-primary bg-primary-soft" : "border-line hover:border-muted"
              )}
            >
              <input
                type="radio"
                name="mfa"
                value="totp"
                checked={mfa === "totp"}
                onChange={() => setMfa("totp")}
                className="size-4 accent-primary"
              />
              <div>
                <div className="text-sm font-bold text-ink">Authenticator App (TOTP)</div>
                <div className="text-xs text-muted">Google Authenticator, Okta Verify, 1Password</div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between border-t border-line pt-5">
        <span className="text-xs text-muted">Need help? Contact support@alloybuild.com</span>
        <Button type="submit" loading={submitting}>Activate Account</Button>
      </div>
    </form>
  );
}

function OrganizationStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <SectionIntro
        title="Organization Details"
        description="Configure the legal entity and operational parameters for this deployment."
      />

      <div className="space-y-4">
        <Field label="Legal Entity Name" htmlFor="org-name" required>
          <Input id="org-name" defaultValue={MOCK_ORG_SETUP.legalName} required />
        </Field>

        <Field label="Jurisdiction" htmlFor="org-jurisdiction" required>
          <Input id="org-jurisdiction" defaultValue={MOCK_ORG_SETUP.jurisdiction} required />
        </Field>

        <Field label="Tenant ID" htmlFor="org-tenant" hint="Auto-generated from legal entity name. Cannot be changed after setup.">
          <Input id="org-tenant" defaultValue={MOCK_ORG_SETUP.tenantId} readOnly className="bg-bg font-mono text-sm text-muted" />
        </Field>

        <Field label="Trust Domain" htmlFor="org-trust-domain" required>
          <Input id="org-trust-domain" defaultValue={MOCK_ORG_SETUP.trustDomain} required />
        </Field>

        <div className="pt-2">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.06em] text-muted">Primary Contacts</div>
          <div className="space-y-4">
            <Field label="Operations Contact" htmlFor="org-ops" required>
              <Input id="org-ops" type="email" defaultValue={MOCK_ORG_SETUP.opsContact} required />
            </Field>
            <Field label="Security Contact" htmlFor="org-security" required>
              <Input id="org-security" type="email" defaultValue={MOCK_ORG_SETUP.securityContact} required />
            </Field>
            <Field label="Compliance Contact" htmlFor="org-compliance" required>
              <Input id="org-compliance" type="email" defaultValue={MOCK_ORG_SETUP.complianceContact} required />
            </Field>
          </div>
        </div>
      </div>

      <StepActions onPrimary={onNext} onSecondary={onBack} />
    </div>
  );
}

function BrandingStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [productName, setProductName] = useState(MOCK_THEME_DEFAULT.productName);
  const [primaryColor, setPrimaryColor] = useState(() => cssVarColor(MOCK_THEME_DEFAULT.primaryColor));
  const [logoFile, setLogoFile] = useState<string | null>(MOCK_THEME_DEFAULT.logoFileName);

  const initial = useMemo(() => productName.trim().charAt(0).toUpperCase() || "H", [productName]);
  const themePresets = useMemo(() => THEME_COLOR_PRESETS.map(cssVarColor), []);

  return (
    <div>
      <SectionIntro
        title="Branding & Theme"
        description="Customize how the Control Plane appears to your users. Changes apply across all apps."
      />

      <div className="space-y-5">
        <Field label="Product Name" htmlFor="brand-name" hint="Shown in the sidebar header and onboarding screens.">
          <Input
            id="brand-name"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            maxLength={40}
          />
        </Field>

        <Field label="Logo" htmlFor="brand-logo">
          <button
            type="button"
            onClick={() => {
              setLogoFile("harbor-trust-logo.svg");
              toast({ variant: "info", title: "Logo upload simulated", description: "In production this opens a file picker." });
            }}
            className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed border-line-strong bg-bg p-8 text-muted transition-colors hover:border-primary hover:bg-primary-soft"
          >
            <Upload aria-hidden className="size-6" />
            <div className="text-sm font-medium">
              {logoFile ? logoFile : "Click to upload SVG or PNG (max 2 MB)"}
            </div>
            {!logoFile && <div className="text-xs">Recommended 256 × 256px</div>}
          </button>
        </Field>

        <Field label="Primary Color" htmlFor="brand-color">
          <div className="flex flex-wrap items-center gap-3">
            <input
              id="brand-color"
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              aria-label="Primary color"
              className="size-9 cursor-pointer rounded border border-line bg-transparent"
            />
            <Input
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="w-28 font-mono text-xs"
              maxLength={7}
            />
            <span className="text-xs text-muted">or pick a preset</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {themePresets.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Select color ${c}`}
                onClick={() => setPrimaryColor(c)}
                className={cn(
                  "size-8 rounded-lg border-2 transition-all",
                  primaryColor.toLowerCase() === c.toLowerCase() ? "border-ink" : "border-transparent"
                )}
                style={{ backgroundColor: c }}
              >
                {primaryColor.toLowerCase() === c.toLowerCase() && (
                  <Check aria-hidden className="mx-auto size-4 text-white" />
                )}
              </button>
            ))}
          </div>
        </Field>

        <div>
          <div className="mb-2 text-xs font-semibold text-ink">Preview</div>
          <div className="overflow-hidden rounded-lg border border-line bg-surface">
            <div className="flex items-center gap-3 border-b border-line bg-bg px-4 py-3">
              <div
                className="grid size-8 place-items-center rounded-md text-sm font-extrabold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {initial}
              </div>
              <span className="text-sm font-bold text-ink">{productName || "Your Organization"}</span>
            </div>
            <div className="space-y-2 p-4">
              <div className="h-2.5 w-3/5 rounded bg-line" />
              <div className="h-2.5 w-4/5 rounded bg-line" />
              <div className="h-2.5 w-1/2 rounded bg-line" />
              <div className="mt-2 h-7 w-28 rounded" style={{ backgroundColor: primaryColor }} />
            </div>
          </div>
        </div>
      </div>

      <StepActions onPrimary={onNext} onSecondary={onBack} />
    </div>
  );
}

function InvitesStep({
  activeTab,
  onTabChange,
  onNext,
  onBack,
}: {
  activeTab: "form" | "pending";
  onTabChange: (tab: "form" | "pending") => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [invites, setInvites] = useState<OrgAdminInvite[]>(MOCK_ORG_ADMIN_INVITES);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState(ORG_INVITE_ROLES[0]);
  const [message, setMessage] = useState("");

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ variant: "error", title: "Please enter an email address" });
      return;
    }
    const newInvite: OrgAdminInvite = {
      id: `INV-${Date.now()}`,
      email,
      firstName,
      lastName,
      role,
      sentAt: "Just now",
      expiresAt: "24h remaining",
      status: "Pending",
    };
    setInvites((prev) => [newInvite, ...prev]);
    setEmail("");
    setFirstName("");
    setLastName("");
    setMessage("");
    onTabChange("pending");
    toast({ variant: "success", title: `Invitation sent to ${email}`, description: LOCAL_ONLY_NOTICE });
  };

  const pendingCount = invites.filter((i) => i.status === "Pending").length;

  const statusBadgeVariant: Record<OrgAdminInvite["status"], "info" | "success" | "neutral"> = {
    Pending: "info",
    Accepted: "success",
    Expired: "neutral",
  };

  return (
    <div>
      <SectionIntro
        title="Invite Org Admins"
        description="Invite additional administrators to manage your organization. They will receive an activation email and must complete MFA setup within 24 hours."
      />

      <InfoBanner>You must invite at least two organization admins to satisfy the admin quorum.</InfoBanner>

      <Tabs value={activeTab} onValueChange={(v) => onTabChange(v as "form" | "pending")}>
        <TabsList>
          <TabsTrigger value="form">Invite Form</TabsTrigger>
          <TabsTrigger value="pending" count={pendingCount}>Pending Invites</TabsTrigger>
        </TabsList>

        <TabsContent value="form" className="pt-4">
          <form onSubmit={handleSend} className="space-y-4">
            <Field label="Email Address" htmlFor="invite-email" required>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="First Name" htmlFor="invite-first">
                <Input id="invite-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Maya" />
              </Field>
              <Field label="Last Name" htmlFor="invite-last">
                <Input id="invite-last" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Okonkwo" />
              </Field>
            </div>

            <Field label="Role" htmlFor="invite-role" hint="Org Admin has full access to all features. Restricted roles are available after setup.">
              <Select id="invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full">
                {ORG_INVITE_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Select>
            </Field>

            <Field label="Personal Message" htmlFor="invite-message">
              <Input
                id="invite-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Welcome to Harbor Trust Bank!"
              />
            </Field>

            <div className="flex gap-3 pt-1">
              <Button type="submit">Send Invitation</Button>
              <Button type="button" variant="secondary" onClick={() => toast({ variant: "info", title: "Bulk invite", description: "Production feature only" })}>
                + Add Another
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="pending" className="pt-4">
          <TableWrapper>
            <Table>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Email</th>
                  <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Role</th>
                  <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Sent</th>
                  <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Expires</th>
                  <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold normal-case tracking-normal text-muted">Status</th>
                  <th className="px-4 py-[9px]" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite.id} className="border-t border-line">
                    <td className="px-4 py-3 text-[13px] font-semibold text-ink">{invite.email}</td>
                    <td className="px-4 py-3"><Badge variant={statusBadgeVariant[invite.status]}>{invite.role}</Badge></td>
                    <td className="px-4 py-3 text-[12px] text-muted">{invite.sentAt}</td>
                    <td className={cn("px-4 py-3 text-[12px]", invite.status === "Expired" ? "text-danger" : "text-muted")}>{invite.expiresAt}</td>
                    <td className="px-4 py-3"><Badge variant={statusBadgeVariant[invite.status]}>{invite.status}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toast({ variant: "success", title: `Resent invitation to ${invite.email}`, description: LOCAL_ONLY_NOTICE })}
                      >
                        Resend
                      </Button>
                    </td>
                  </tr>
                ))}
                {invites.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted">No pending invitations.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </TabsContent>
      </Tabs>

      <div className="mt-8 flex items-center justify-between border-t border-line pt-5">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <div className="flex items-center gap-3">
          <Link to="/admin/users" className="text-sm font-semibold text-primary hover:underline">View all users →</Link>
          <Button onClick={onNext}>Continue</Button>
        </div>
      </div>
    </div>
  );
}

function ReadinessStep({
  ready,
  onReadyChange,
  onBack,
}: {
  ready: boolean;
  onReadyChange: (state: "ready" | "incomplete") => void;
  onBack: () => void;
}) {
  const checklist: ReadinessCheckItem[] = ready ? MOCK_READINESS_CHECKLIST : MOCK_READINESS_INCOMPLETE_CHECKLIST;
  const passedCount = checklist.filter((c) => c.passed).length;
  const totalCount = checklist.length;
  const percent = Math.round((passedCount / totalCount) * 100);

  return (
    <div>
      <SectionIntro
        title="Production Readiness Gate"
        description={ready ? "All requirements met. You may request production access." : "Complete all items below before requesting production access."}
      />

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => onReadyChange("incomplete")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
            !ready ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted hover:text-ink"
          )}
        >
          Incomplete
        </button>
        <button
          type="button"
          onClick={() => onReadyChange("ready")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
            ready ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted hover:text-ink"
          )}
        >
          Ready
        </button>
      </div>

      <Progress value={percent} tone={ready ? "success" : "warning"} label="Readiness" />

      <div className="mt-4 text-sm text-text">
        <strong className="text-ink">{passedCount} of {totalCount}</strong> requirements met
      </div>

      {ready ? (
        <div className="mt-4 rounded-lg border border-success-border bg-success-soft px-4 py-3 text-center text-sm font-semibold text-success">
          ✓ All requirements satisfied — production access available
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-warning-border bg-warning-soft px-4 py-3 text-center text-sm font-semibold text-warning">
          ⚠ {totalCount - passedCount} item{totalCount - passedCount === 1 ? "" : "s"} incomplete — production access is blocked
        </div>
      )}

      <Card className="mt-5">
        <CardBody>
          <ul className="divide-y divide-line">
            {checklist.map((item) => (
              <li key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <span
                  className={cn(
                    "grid size-6 shrink-0 place-items-center rounded-full border text-xs font-bold",
                    item.passed
                      ? "border-success-border bg-success-soft text-success"
                      : "border-danger-border bg-danger-soft text-danger"
                  )}
                >
                  {item.passed ? <Check aria-hidden className="size-3.5" /> : <X aria-hidden className="size-3.5" />}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-ink">{item.label}</div>
                  <div className="text-xs text-muted">{item.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <div className="mt-8 flex items-center justify-between border-t border-line pt-5">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button
          disabled={!ready}
          onClick={() => toast({ variant: "success", title: "Production access request submitted", description: localOnly("Pending approval (P6).") })}
        >
          Request Production Access
        </Button>
      </div>
    </div>
  );
}

function useOrgQueryParams(): {
  step: OrgBootstrapStep;
  invitesTab: "form" | "pending";
  readinessState: "ready" | "incomplete";
  setStep: (next: OrgBootstrapStep) => void;
  setInvitesTab: (tab: "form" | "pending") => void;
  setReadinessState: (state: "ready" | "incomplete") => void;
} {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawStep = Number(searchParams.get("step") ?? 0);
  const step = (Number.isNaN(rawStep) ? 0 : Math.max(0, Math.min(4, rawStep))) as OrgBootstrapStep;

  const invitesTab = searchParams.get("tab") === "pending" ? "pending" : "form";
  const readinessState = searchParams.get("state") === "incomplete" ? "incomplete" : "ready";

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams.toString());
    params.set(key, value);
    navigate(`/admin/org?${params.toString()}`, { replace: true });
  }

  return {
    step,
    invitesTab,
    readinessState,
    setStep: (next) => setParam("step", String(next)),
    setInvitesTab: (tab) => setParam("tab", tab),
    setReadinessState: (state) => setParam("state", state),
  };
}

export default function OrgBootstrapWizard() {
  const { step, invitesTab, readinessState, setStep, setInvitesTab, setReadinessState } = useOrgQueryParams();

  return (
    <div>
      <div className="mb-5 flex items-start gap-3">
        <div className="grid size-10 place-items-center rounded-lg bg-primary-soft">
          <Building2 aria-hidden className="size-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-[1.2] text-ink">Organization Bootstrap</h1>
          <p className="text-xs text-muted">ORG-00..04 · DEP-001 · Harbor Trust Bank, N.A.</p>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-line bg-surface p-4 shadow-soft">
        <Stepper steps={STEPS} current={step} />
      </div>

      <Card className="max-w-[760px]">
        <CardBody className="p-6 md:p-8">
          {step === 0 && <ActivateStep onNext={() => setStep(1)} />}
          {step === 1 && <OrganizationStep onNext={() => setStep(2)} onBack={() => setStep(0)} />}
          {step === 2 && <BrandingStep onNext={() => setStep(3)} onBack={() => setStep(1)} />}
          {step === 3 && (
            <InvitesStep
              activeTab={invitesTab}
              onTabChange={setInvitesTab}
              onNext={() => setStep(4)}
              onBack={() => setStep(2)}
            />
          )}
          {step === 4 && (
            <ReadinessStep
              ready={readinessState === "ready"}
              onReadyChange={setReadinessState}
              onBack={() => setStep(3)}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
